import React, { useState, useEffect, useCallback } from 'react';
import { superAdmin } from '../api';
import LoadingSpinner from './LoadingSpinner';
import { ShieldOff, ShieldCheck, Plus, Trash2, AlertCircle, CheckCircle2, Search } from 'lucide-react';

// Super admin "IP Based Blocking" tab. Entries apply platform-wide: a matching
// visitor is refused by every chat, voice, combo and live-avatar widget.
const IPV4 = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
const IPV6 = /^[0-9a-fA-F:]+(\/\d{1,3})?$/;

const errorText = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join(', ');
  return (typeof detail === 'string' && detail) || err?.message || fallback;
};

const formatDate = (iso) => (iso ? new Date(iso + (iso.endsWith('Z') ? '' : 'Z')).toLocaleString() : '—');

const IPBlockingPanel = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [ipAddress, setIpAddress] = useState('');
  const [reason, setReason] = useState('');
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superAdmin.listBlockedIps();
      setEntries(res.data.blocked_ips || []);
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Failed to load blocked IPs'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const value = ipAddress.trim();
    if (!IPV4.test(value) && !IPV6.test(value)) {
      setError('Enter a valid IPv4/IPv6 address or CIDR range, e.g. 203.0.113.7 or 203.0.113.0/24');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await superAdmin.addBlockedIp({ ip_address: value, reason: reason.trim() || null });
      setIpAddress('');
      setReason('');
      flash(`${value} is now blocked on every widget`);
      await load();
    } catch (err) {
      setError(errorText(err, 'Failed to block IP'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (entry) => {
    try {
      await superAdmin.updateBlockedIp(entry.id, { is_active: !entry.is_active });
      flash(entry.is_active ? `${entry.ip_address} paused` : `${entry.ip_address} blocked again`);
      await load();
    } catch (err) {
      setError(errorText(err, 'Failed to update block'));
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Unblock ${entry.ip_address}? Widgets will accept this visitor again.`)) return;
    try {
      await superAdmin.deleteBlockedIp(entry.id);
      flash(`${entry.ip_address} unblocked`);
      await load();
    } catch (err) {
      setError(errorText(err, 'Failed to unblock IP'));
    }
  };

  const visible = entries.filter((e) => {
    const q = filter.trim().toLowerCase();
    return !q || e.ip_address.toLowerCase().includes(q) || (e.reason || '').toLowerCase().includes(q);
  });

  const inputClass = 'w-full px-4 py-3 bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground mb-1">IP Based Blocking</h1>
        <p className="text-sm text-muted-foreground">
          Blocked IPs cannot start or continue a conversation on any chat, voice, combo or live avatar widget, across every organization.
        </p>
      </div>

      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>{error}</div>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-500/10 border border-green-500/20 text-green-500 rounded-xl flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="font-medium">{success}</div>
        </div>
      )}

      <form onSubmit={handleAdd} className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6 shadow-lg shadow-primary/5">
        <div className="grid gap-4 md:grid-cols-[1fr_1.5fr_auto] md:items-end">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">IP address or CIDR range</label>
            <input
              type="text"
              value={ipAddress}
              onChange={(e) => setIpAddress(e.target.value)}
              className={`${inputClass} font-mono`}
              placeholder="203.0.113.7 or 203.0.113.0/24"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Reason (optional)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={inputClass}
              placeholder="e.g. Abusive caller, spam traffic"
              maxLength={500}
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-destructive text-destructive-foreground rounded-xl hover:bg-destructive/90 transition-all duration-300 font-medium disabled:opacity-60"
          >
            <Plus className="w-4 h-4" />
            {saving ? 'Blocking...' : 'Block IP'}
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Takes effect within about 15 seconds. Conversations already in progress are not cut off, but the visitor cannot send new chat messages or start a new call.
        </p>
      </form>

      <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6 shadow-lg shadow-primary/5">
        <div className="flex items-center justify-between gap-4 mb-4">
          <h2 className="text-lg font-semibold text-foreground">
            Blocked IPs <span className="text-muted-foreground font-normal">({entries.filter((e) => e.is_active).length} active)</span>
          </h2>
          <div className="relative w-64 max-w-full">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-background/50 border border-border/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="Search IP or reason"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><LoadingSpinner /></div>
        ) : visible.length === 0 ? (
          <div className="text-center py-10">
            <ShieldCheck className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">{entries.length === 0 ? 'No IPs are blocked.' : 'No matches.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border/50">
                  <th className="py-2 pr-4 font-medium">IP / Range</th>
                  <th className="py-2 pr-4 font-medium">Reason</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Blocked requests</th>
                  <th className="py-2 pr-4 font-medium">Added</th>
                  <th className="py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((e) => (
                  <tr key={e.id} className="border-b border-border/30 last:border-0">
                    <td className="py-3 pr-4 font-mono text-foreground whitespace-nowrap">
                      {e.ip_address}
                      {e.is_range && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-sans">range</span>}
                    </td>
                    <td className="py-3 pr-4 text-muted-foreground max-w-xs truncate" title={e.reason || ''}>{e.reason || '—'}</td>
                    <td className="py-3 pr-4">
                      {e.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-destructive/15 text-destructive rounded-lg text-xs font-medium">
                          <ShieldOff className="w-3 h-3" /> Blocked
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-muted text-muted-foreground rounded-lg text-xs font-medium">Paused</span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-muted-foreground whitespace-nowrap">
                      {e.hit_count}
                      {e.last_blocked_at && <div className="text-xs">last {formatDate(e.last_blocked_at)}</div>}
                    </td>
                    <td className="py-3 pr-4 text-muted-foreground whitespace-nowrap">
                      {formatDate(e.created_at)}
                      {e.created_by && <div className="text-xs">by {e.created_by}</div>}
                    </td>
                    <td className="py-3 text-right whitespace-nowrap">
                      <button onClick={() => handleToggle(e)} className="text-xs text-muted-foreground hover:text-foreground hover:underline mr-4">
                        {e.is_active ? 'Pause' : 'Resume'}
                      </button>
                      <button onClick={() => handleDelete(e)} className="inline-flex items-center gap-1 text-xs text-destructive hover:text-destructive/80 hover:underline">
                        <Trash2 className="w-3 h-3" /> Unblock
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default IPBlockingPanel;
